import { z } from 'zod';
import { CRYPTO_PROVIDER } from '../types/enums';
import { cryptoWithdrawalDetailsSchema } from './cryptoWithdrawal';

// Wallet contract for 0xProcessing withdrawals. Types are inferred from these schemas so the
// runtime check at the network boundary and the compile-time shape cannot drift apart.
const decimal = z.string().regex(/^(0|[1-9]\d{0,29})(\.\d{1,18})?$/);

export const withdrawalStateSchema = z.enum([
  'awaiting_approval',
  'submitting',
  'pending',
  'unknown',
  'review',
  'confirmed',
  'aborted',
  'rejected',
]);

export const withdrawalCurrenciesSchema = z.object({
  currencies: z.array(
    z.object({
      id: z.string().min(1),
      label: z.string().min(1),
      requiresTag: z.boolean(),
      minimumWithdrawalAmount: decimal,
      estimatedNetworkFee: decimal,
    })
  ),
});

export const withdrawalPreviewSchema = z.object({
  debit: decimal,
  accountCurrency: z.string().regex(/^[A-Z]{3}$/),
  currency: z.string().min(1),
  assetAmount: decimal,
  estimatedNetworkFee: decimal,
  estimatedReceived: decimal,
  address: z.string().min(1),
  tag: z.string().nullable(),
  confirmationToken: z.string().min(1),
  expiresAt: z.iso.datetime({ offset: true }),
});

export const withdrawalResultSchema = z.object({
  transactionId: z.uuid(),
  status: withdrawalStateSchema,
  debit: decimal,
  accountCurrency: z.string(),
  assetAmount: decimal,
  estimatedNetworkFee: decimal,
  estimatedReceived: decimal,
  failureReason: z.string().nullable(),
});

export const cryptoWithdrawalMetadataSchema = cryptoWithdrawalDetailsSchema.extend({
  provider: z.literal(CRYPTO_PROVIDER.ZERO_X_PROCESSING),
  state: withdrawalStateSchema,
  providerWithdrawalId: z.string().optional(),
});

export type ZeroXWithdrawalState = z.infer<typeof withdrawalStateSchema>;

export type ZeroXWithdrawalCurrency = z.infer<typeof withdrawalCurrenciesSchema>['currencies'][number];

export type ZeroXWithdrawalPreview = z.infer<typeof withdrawalPreviewSchema>;

export type ZeroXWithdrawalResult = z.infer<typeof withdrawalResultSchema>;

export type ZeroXWithdrawalMetadata = z.infer<typeof cryptoWithdrawalMetadataSchema>;

export type ZeroXWithdrawalInput = { amount: string; currency: string; address: string; tag?: string };
